// #region Imports
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { useGetFeeSchedulesForTeamQuery } from "marketplace/teams/fee-schedules/_api/fee-schedule-api";
import { getFeeSchedulesForTeam } from "marketplace/teams/fee-schedules/_stores/fee-schedule-redux-slice";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
// The categories table is named for the work domain's templates, but it is the app's one editable
// and read-only fee schedule categories table — the shared template page and the proposal fee
// schedule tab both drive it the same way. Reusing it keeps a team's published rates looking like
// rates everywhere else in the app.
import FeeScheduleCategoriesTable from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  width: "100%",
}));
const SectionHeading = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: "1.3em",
}));
const ScheduleSummary = styled(AccordionSummary)(({ theme }) => ({
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    margin: 0,
  },
}));
const ScheduleName = styled(Typography)(() => ({
  fontWeight: 600,
}));
const CategoryCount = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginLeft: "auto",
  paddingLeft: theme.spacing(2),
}));
const CategoriesTable = styled(FeeScheduleCategoriesTable)(() => ({
  maxWidth: "100%",
  minWidth: 0,
}));
// #endregion

// #region Component Input Props
export type TeamFeeSchedulesProps = {
  className?: string;
  teamId: Guid;
};
// #endregion

/**
 * The fee schedules a team publishes on its marketplace profile.
 *
 * Publishing rates is optional, so a team that has none renders nothing at all rather than an empty
 * section — the heading only appears once there is something under it to read.
 */
export default function TeamFeeSchedules(props: Readonly<TeamFeeSchedulesProps>) {
  const { className, teamId } = props;

  // #region Hooks
  const { isError, isLoading } = useGetFeeSchedulesForTeamQuery({ teamId: teamId.value });
  const feeSchedulesJson = useSelector(getFeeSchedulesForTeam(teamId));

  // The table takes the domain objects, and a fresh array each render would reach its hook
  // dependencies. Memoized deliberately so the tables don't remount on every profile render.
  const feeSchedules = useMemo(
    () => (feeSchedulesJson ? FeeSchedule.fromJSONArray(feeSchedulesJson) : []),
    [feeSchedulesJson]
  );

  // A failed load leaves the store empty, which renders exactly like a team that publishes no
  // rates. Say so, otherwise the reader is told this team has no rates when the truth is we could
  // not fetch them.
  useEffect(() => {
    if (isError) {
      enqueueSnackbar("Couldn't load this team's fee schedules", { variant: "warning" });
    }
  }, [isError]);
  // #endregion

  if (isLoading) {
    return (
      <MainContainer className={className}>
        <SectionHeading>Fee Schedules</SectionHeading>
        <Loader />
      </MainContainer>
    );
  }

  if (feeSchedules.length < 1) return null;

  // #region Render
  return (
    <MainContainer className={className}>
      <SectionHeading>Fee Schedules</SectionHeading>
      {feeSchedules.map((feeSchedule: FeeSchedule) => (
        <Accordion
          key={feeSchedule.id.value}
          disableGutters
          // The tables are only built once a reader opens the schedule they care about, so a team
          // publishing several of them doesn't mount several data grids up front.
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <ScheduleSummary expandIcon={<ExpandMoreIcon />}>
            <ScheduleName>{feeSchedule.name}</ScheduleName>
            {feeSchedule.tags.map((tag) => (
              <Chip key={tag.value} label={tag.value} size="small" variant="outlined" />
            ))}
            <CategoryCount variant="body2">
              {feeSchedule.categoryCountLabel}
            </CategoryCount>
          </ScheduleSummary>
          <AccordionDetails>
            <CategoriesTable
              selectedCategories={feeSchedule.categories}
              disableEditing
              disableCategorySelection
              hideActionsColumn
              hideFieldHistory
              hideAddButton
              hideExistingButton
              hideFromTemplateButton
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </MainContainer>
  );
  // #endregion
}
